import { Box, Typography } from '@mui/material';
import DownloadPDFModal from '../../../../components/DownloadPDFModal/DownloadPDFModal';
import { FundingEligibilityChartResponse, Game, Metric } from './types';
import { fundingEligibilityMetricsTooltips } from './constants';
import FundingEligibilityMetricBox from './FundingEligibilityMetricBox';
import FundingEligibilityInfo from './FundingEligibilityInfo';
import PdfHeader from '../../../../components/DownloadPDFModal/PdfHeader';

interface FundEligibilityPdfProps {
  isOpen: boolean;
  handleClose: () => void;
  chartData: FundingEligibilityChartResponse | undefined;
  selectedGame: Game | undefined;
}

const FundEligibilityPdf: React.FC<FundEligibilityPdfProps> = ({
  isOpen,
  handleClose,
  chartData,
  selectedGame,
}) => {
  const customAttributes = chartData?.metadata?.customAttributes;

  const getMetricType = (metric: Metric) => {
    if (customAttributes?.[metric.metric]) return customAttributes?.[metric.metric];
    else return 'percentage';
  };

  const filteredMetrics = chartData?.metrics?.filter((metric) => metric !== null);

  return (
    <>
      <DownloadPDFModal
        isOpen={isOpen}
        handleClose={handleClose}
        fileName={`${chartData?.name}_${selectedGame?.name}.pdf`}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: filteredMetrics && filteredMetrics?.length > 6 ? '106rem' : 'auto',
            }}
          >
            <PdfHeader />
            <Box
              sx={{
                padding: '1.25rem',
                backgroundColor: '#FFFFFF',
                borderRadius: '0.75rem',
                marginBottom: '1.125rem',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.375rem',
                  color: '#1B1C17',
                }}
              >
                Funding Eligibility Report
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#6C6C6C',
                }}
              >
                {selectedGame?.name}
              </Typography>
            </Box>
            <FundingEligibilityInfo chartData={chartData} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1rem',
                marginY: '1.125rem',
              }}
            >
              {filteredMetrics?.map((metric: Metric, index: number) => (
                <FundingEligibilityMetricBox
                  key={index}
                  title={metric?.displayName}
                  value={metric?.referenceValue}
                  status={metric?.score}
                  weightage={metric?.weightage}
                  scoreType={getMetricType(metric)}
                />
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              marginTop: '20px',
            }}
          >
            <Typography
              sx={{
                color: '#1B1C17',
                fontSize: '1.25rem',
                lineHeight: '1.375rem',
                fontWeight: 700,
                marginBottom: '0.75rem',
              }}
            >
              Definitions
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              {fundingEligibilityMetricsTooltips.map((metric, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      lineHeight: '1.25rem',
                      fontWeight: 500,
                      color: '#162936',
                    }}
                  >
                    {metric.id}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      lineHeight: '1.25rem',
                      fontWeight: 500,
                      color: '#6C6C6C',
                    }}
                  >
                    {`${metric.content} ${metric.example}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DownloadPDFModal>
    </>
  );
};

export default FundEligibilityPdf;
